/**
 * Doc-scoped variable create / table edits routed through the block undo stack,
 * plus derived flags used by the Variables block and toolbar.
 */

import { useCallback, useMemo, useRef } from 'react';
import { hasVariablesBlock as docHasVariablesBlock } from '../utils/blockUtils';
import { flattenBlocksDeep } from '../utils/tableCellBlocks';
import {
  campaignDefinedVariables,
  clearUnlockedVariableValues,
  clearVariableRowValues,
  countFilledVariableSlots,
  generateVariableId,
  countMissionDisplayedFilledSlots,
  docRowScopeChoice,
  emptyMissionViewVariableValues,
  hasUnlockedFilledVariableValues,
  nextVariableColor,
  presentDocVariable,
  removeDocValueOverlays,
  variableNameKey,
} from '../../../utils/variableScope';
import { getSharedRowsRef } from '../../VariablesPanel/VariablesTable/hooks/sharedVariableRowsRef';
import { buildVariableOfType } from '../../../utils/variableTypeChange';
import {
  renameVarsInBlocks,
  renameVarsInVariableRows,
} from '../utils/variableUtils';
import { showToast } from '../../Toast';
import { isTickActive, readDisplayedTick } from '../utils/tickOverrides';

export function useDocEditorVariables({
  blocks,
  tickOverrides,
  commitDocVariables,
  commitDocVariablesAndBlocks,
  docVariables,
  docVariablesRef,
  blocksRef,
  undo,
  onDocVariablesChange,
  onCreateVariable,
  onCampaignVariablesChange,
  variableCampaign,
  allSprints,
  missionVariables,
  onMissionVariablesChange,
}) {
  const writeToMission = typeof onMissionVariablesChange === 'function';
  const writeToCampaign = !!(onCampaignVariablesChange && variableCampaign) && !writeToMission;
  const campaignVarsRef = useRef(variableCampaign?.variables);
  campaignVarsRef.current = variableCampaign?.variables;
  const onCampChangeRef = useRef(onCampaignVariablesChange);
  onCampChangeRef.current = onCampaignVariablesChange;
  const missionVarsRef = useRef(missionVariables);
  missionVarsRef.current = missionVariables;
  const onMissionChangeRef = useRef(onMissionVariablesChange);
  onMissionChangeRef.current = onMissionVariablesChange;
  // Variables table edits share the block undo stack. Typing coalesces;
  // structural edits / paste (via separateNextUndoStep) get their own step.
  const handleDocVariablesChange = useCallback(
    (vars, { silent = false } = {}) => {
      commitDocVariables(vars, { coalesce: silent });
    },
    [commitDocVariables]
  );

  // "{{" picker create — always a doc declaration. Values copy onto the
  // campaign later, when edited through a campaign.
  const handleCreateVariable = useCallback(
    (name, type) => {
      const trimmed = String(name || '')
        .trim()
        .replace(/\s+/g, '_');
      if (!trimmed) return;
      if (!onDocVariablesChange) {
        onCreateVariable?.(trimmed, type);
        return;
      }
      const prev = docVariablesRef.current || [];
      if (
        prev.some((v) => variableNameKey(v.name) === variableNameKey(trimmed))
      ) {
        return;
      }
      commitDocVariables(
        [
          ...prev,
          buildVariableOfType(
            {
              id: generateVariableId(),
              name: trimmed,
              description: '',
              color: nextVariableColor(prev),
              locked: false,
            },
            type
          ),
        ],
        { coalesce: false }
      );
    },
    [onDocVariablesChange, onCreateVariable, commitDocVariables, docVariablesRef]
  );

  /**
   * Rename a doc variable and rewrite pills / {{tokens}} / nested value refs
   * in one undo step. Called from the Variables table on name-field blur.
   */
  const handleRenameVariable = useCallback(
    (scope, { id, oldName, newName }) => {
      if (scope !== 'doc' || !onDocVariablesChange || !commitDocVariablesAndBlocks) {
        return false;
      }
      const trimmed = String(newName || '')
        .trim()
        .replace(/\s+/g, '_');
      const from = String(oldName || '').trim();
      // Empty → name is initial naming, not a rename (no refs to rewrite)
      if (!trimmed || !from || from === trimmed) return false;

      const prev = docVariablesRef.current || [];
      if (
        prev.some(
          (v) => v.id !== id && variableNameKey(v.name) === variableNameKey(trimmed)
        )
      ) {
        // Revert the silent name draft back to the pre-focus name
        const reverted = prev.map((v) => (v.id === id ? { ...v, name: from } : v));
        commitDocVariables(reverted, { coalesce: false, recordEpoch: true });
        showToast(`“${trimmed}” already exists`, { type: 'error' });
        return 'conflict';
      }

      const missionPrev = missionVarsRef.current || [];
      const campPrev = campaignVarsRef.current || [];
      const row = prev.find((v) => v.id === id);
      const viewCtx = {
        campaign: variableCampaign
          ? { ...variableCampaign, variables: campPrev }
          : null,
        allSprints,
        missionVariables: writeToMission ? missionPrev : null,
      };
      let base = prev;
      let nextMission = missionPrev;
      let nextCamp = campPrev;
      if (row && docRowScopeChoice(row, viewCtx.missionVariables) === 'doc') {
        const view = presentDocVariable(row, viewCtx);
        base = prev.map((v) =>
          v.id === id
            ? {
                ...v,
                value: view?.value ?? v.value,
                ...(Array.isArray(view?.values) ? { values: [...view.values] } : {}),
                ...(view?.type ? { type: view.type } : {}),
                valueScope: 'doc',
              }
            : v
        );
        nextMission = removeDocValueOverlays(missionPrev, row);
        nextCamp = removeDocValueOverlays(campPrev, row);
      } else if (row) {
        const renameCopies = (rows) => {
          const target = (rows || []).find(
            (v) =>
              v &&
              (v.id === id ||
                (v.docOverlay && variableNameKey(v.name) === variableNameKey(from)))
          );
          return renameVarsInVariableRows(rows || [], from, trimmed, {
            renamedId: target?.id || null,
          }).rows;
        };
        nextMission = renameCopies(missionPrev);
        nextCamp = renameCopies(campPrev);
      }
      nextMission = renameVarsInVariableRows(nextMission, from, trimmed).rows;
      nextCamp = renameVarsInVariableRows(nextCamp, from, trimmed).rows;

      const undoDocVariables = base.map((v) =>
        v.id === id ? { ...v, name: from } : v
      );
      const { rows, count: rowCount } = renameVarsInVariableRows(
        base,
        from,
        trimmed,
        { renamedId: id }
      );
      const sharedDocs = getSharedRowsRef(handleDocVariablesChange, rows);
      if (sharedDocs) sharedDocs.current = rows;
      const listChanged = (prevList, nextList) =>
        prevList.length !== nextList.length ||
        prevList.some((row, i) => row !== nextList[i]);
      if (writeToMission && onMissionChangeRef.current && listChanged(missionPrev, nextMission)) {
        const sharedMission = getSharedRowsRef(onMissionChangeRef.current, missionPrev);
        if (sharedMission) sharedMission.current = nextMission;
        missionVarsRef.current = nextMission;
        onMissionChangeRef.current(nextMission);
      }
      if (onCampChangeRef.current && variableCampaign && listChanged(campPrev, nextCamp)) {
        const sharedCamp = getSharedRowsRef(onCampChangeRef.current, campPrev);
        if (sharedCamp) sharedCamp.current = nextCamp;
        campaignVarsRef.current = nextCamp;
        onCampChangeRef.current(nextCamp);
      }
      // Prefer live ref; fall back to last rendered tree if ref was clobbered
      const sourceBlocks = blocksRef?.current ?? blocks;
      const { blocks: nextBlocks, count: blockCount } = renameVarsInBlocks(
        sourceBlocks,
        from,
        trimmed
      );
      const total = rowCount + blockCount;

      commitDocVariablesAndBlocks(rows, nextBlocks, { undoDocVariables });

      showToast(
        total > 0
          ? `Renamed ${from} → ${trimmed} · updated ${total} reference${
              total === 1 ? '' : 's'
            }`
          : `Renamed ${from} → ${trimmed}`,
        {
          type: 'success',
          duration: 6000,
          onClick: () => undo?.(),
          actionHint: 'Undo',
        }
      );
      return true;
    },
    [
      onDocVariablesChange,
      commitDocVariablesAndBlocks,
      commitDocVariables,
      docVariablesRef,
      blocksRef,
      blocks,
      undo,
      writeToMission,
      variableCampaign,
      allSprints,
      handleDocVariablesChange,
    ]
  );

  const anyChecked = useMemo(
    () =>
      flattenBlocksDeep(blocks).some((b) => {
        if (b.type !== 'checkbox') return false;
        return isTickActive(readDisplayedTick(b, tickOverrides));
      }),
    [blocks, tickOverrides]
  );

  const canEmptyVariableValues = useMemo(
    () => {
      if (writeToMission) {
        return countMissionDisplayedFilledSlots(docVariables, {
          campaign: variableCampaign,
          allSprints,
          missionVariables,
        }) > 0;
      }
      if (writeToCampaign) {
        const ctx = {
          campaign: variableCampaign,
          allSprints,
          missionVariables: null,
        };
        const docFilled = (docVariables || []).some(
          (v) => v && !v.locked && countFilledVariableSlots(presentDocVariable(v, ctx)) > 0
        );
        const campFilled = campaignDefinedVariables(variableCampaign?.variables).some(
          (v) => v && !v.locked && countFilledVariableSlots(v) > 0
        );
        return docFilled || campFilled;
      }
      return !!onDocVariablesChange && hasUnlockedFilledVariableValues(docVariables);
    },
    [
      writeToMission,
      writeToCampaign,
      variableCampaign,
      allSprints,
      missionVariables,
      docVariables,
      onDocVariablesChange,
    ]
  );

  const emptyVariableValues = useCallback(() => {
    if (writeToMission && onMissionChangeRef.current) {
      const prevMission = missionVarsRef.current || [];
      const prevCamp = campaignVarsRef.current || [];
      const docs = docVariablesRef.current || [];
      const ctx = {
        campaign: variableCampaign
          ? { ...variableCampaign, variables: prevCamp }
          : null,
        allSprints,
        missionVariables: prevMission,
      };
      const cleared = countMissionDisplayedFilledSlots(docs, ctx);
      if (cleared === 0) return;
      const nextDocs = docs.map((v) => {
        if (!v || v.locked) return v;
        if (docRowScopeChoice(v, prevMission) === 'mission') return v;
        if (countFilledVariableSlots(presentDocVariable(v, ctx)) === 0) return v;
        return { ...clearVariableRowValues(v), valueScope: 'doc' };
      });
      const nextMission = emptyMissionViewVariableValues(prevMission, docs, ctx);
      let nextCamp = prevCamp;
      for (const v of docs) {
        if (!v || v.locked) continue;
        if (docRowScopeChoice(v, prevMission) === 'mission') continue;
        nextCamp = removeDocValueOverlays(nextCamp, v);
      }
      const sharedMission = getSharedRowsRef(onMissionChangeRef.current, prevMission);
      if (sharedMission) sharedMission.current = nextMission;
      missionVarsRef.current = nextMission;
      onMissionChangeRef.current(nextMission);
      // Skip an unchanged campaign write. That writer replaces the whole sprint
      // from a ref that has not seen this mission clear yet, so calling it
      // puts the filled This mission values back.
      if (onCampChangeRef.current && nextCamp !== prevCamp) {
        const sharedCamp = getSharedRowsRef(onCampChangeRef.current, prevCamp);
        if (sharedCamp) sharedCamp.current = nextCamp;
        campaignVarsRef.current = nextCamp;
        onCampChangeRef.current(nextCamp);
      }
      const docChanged = nextDocs.some((v, i) => v !== docs[i]);
      if (docChanged) {
        const sharedDocs = getSharedRowsRef(handleDocVariablesChange, docs);
        if (sharedDocs) sharedDocs.current = nextDocs;
        commitDocVariables(nextDocs, { coalesce: false, recordEpoch: true });
      }
      showToast(
        `Emptied ${cleared} variable value${cleared === 1 ? '' : 's'}`,
        {
          type: 'success',
          duration: 6000,
          onClick: () => {
            onMissionChangeRef.current?.(prevMission);
            onCampChangeRef.current?.(prevCamp);
            if (docChanged) undo?.();
          },
          actionHint: 'Undo',
        }
      );
      return;
    }
    if (writeToCampaign && onCampChangeRef.current) {
      const prevCamp = campaignVarsRef.current || [];
      const docs = docVariablesRef.current || [];
      const ctx = {
        campaign: variableCampaign
          ? { ...variableCampaign, variables: prevCamp }
          : null,
        allSprints,
        missionVariables: null,
      };
      let cleared = 0;
      const nextDocs = docs.map((v) => {
        if (!v || v.locked) return v;
        const n = countFilledVariableSlots(presentDocVariable(v, ctx));
        if (n === 0) return v;
        cleared += n;
        return { ...clearVariableRowValues(v), valueScope: 'doc' };
      });
      const docIds = new Set(docs.map((v) => v?.id).filter(Boolean));
      let nextCamp = prevCamp.map((row) => {
        if (!row || row.locked || row.docOverlay || docIds.has(row.id)) return row;
        const n = countFilledVariableSlots(row);
        if (n === 0) return row;
        cleared += n;
        return clearVariableRowValues(row);
      });
      for (const v of docs) {
        if (!v || v.locked) continue;
        nextCamp = removeDocValueOverlays(nextCamp, v);
      }
      if (cleared === 0) return;
      const sharedCamp = getSharedRowsRef(onCampChangeRef.current, prevCamp);
      if (sharedCamp) sharedCamp.current = nextCamp;
      campaignVarsRef.current = nextCamp;
      onCampChangeRef.current(nextCamp);
      const docChanged = nextDocs.some((v, i) => v !== docs[i]);
      if (docChanged) {
        const sharedDocs = getSharedRowsRef(handleDocVariablesChange, docs);
        if (sharedDocs) sharedDocs.current = nextDocs;
        commitDocVariables(nextDocs, { coalesce: false, recordEpoch: true });
      }
      showToast(
        `Emptied ${cleared} variable value${cleared === 1 ? '' : 's'}`,
        {
          type: 'success',
          duration: 6000,
          onClick: () => {
            onCampChangeRef.current?.(prevCamp);
            if (docChanged) undo?.();
          },
          actionHint: 'Undo',
        }
      );
      return;
    }
    if (!onDocVariablesChange) return;
    const prev = docVariablesRef.current || [];
    const next = clearUnlockedVariableValues(prev);
    const cleared = prev.reduce((n, v) => {
      if (!v || v.locked) return n;
      return n + countFilledVariableSlots(v);
    }, 0);
    if (cleared === 0) return;
    // Keep the in-doc table's live list in sync so a focused cell can't
    // blur-commit a stale filled value over the wipe.
    const shared = getSharedRowsRef(handleDocVariablesChange, prev);
    if (shared) shared.current = next;
    commitDocVariables(next, { coalesce: false, recordEpoch: true });
    showToast(
      `Emptied ${cleared} variable value${cleared === 1 ? '' : 's'}`,
      {
        type: 'success',
        duration: 6000,
        onClick: () => undo?.(),
        actionHint: 'Undo',
      }
    );
  }, [
    writeToMission,
    writeToCampaign,
    variableCampaign,
    allSprints,
    onDocVariablesChange,
    commitDocVariables,
    docVariablesRef,
    handleDocVariablesChange,
    undo,
  ]);

  const hasVariablesBlock = useMemo(() => docHasVariablesBlock(blocks), [blocks]);

  return {
    handleDocVariablesChange,
    handleCreateVariable,
    handleRenameVariable,
    emptyVariableValues,
    anyChecked,
    canEmptyVariableValues,
    hasVariablesBlock,
  };
}
